import { useRef, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import {
  ChatContainer,
  ChatHeader,
  ChatHeaderContent,
  ChatPaper,
  LoadingContainer,
  SendMessageContainer,
  SendMessageContent,
  Suggestion,
  SuggestionsContainer,
} from './styles';
import { Text } from '@/components/atoms/text';
import { Button, IconButton } from '@/components/atoms/Button';
import { StyledInput } from '@/components/atoms/inputs/styled';
import { Minimize, Send, WarningAmberOutlined } from '@mui/icons-material';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { ChatMessage } from './message';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { Disclaimer } from '@/components/molecules/disclaimer';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

interface ChatProps {
  onMinimize: () => void;
  code_municipality?: string;
  municipalityName?: string;
  messages: Message[];
  loading: boolean;
  input: string;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onSuggestionClick: (suggestion: string) => void;
  streamingResponse?: string;
  error?: string | null;
}

export const Chat = ({
  onMinimize,
  municipalityName,
  messages,
  loading,
  input,
  onInputChange,
  onSendMessage,
  onSuggestionClick,
  streamingResponse,
  error,
}: ChatProps) => {
  const { t } = useTranslation();

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, streamingResponse, error]);

  return (
    <ChatContainer>
      <ChatHeader>
        <ChatHeaderContent>
          <SparklesIcon color="primary" />
          <Text variant="h6">
            <SafeMarkdown text={t('page.chat.chat.title')} />
          </Text>
        </ChatHeaderContent>

        <IconButton sx={(theme) => ({ color: theme.palette.text.primary })} onClick={onMinimize}>
          <Minimize color="inherit" />
        </IconButton>
      </ChatHeader>

      <ChatPaper variant="outlined">
        <Disclaimer
          icon={WarningAmberOutlined}
          text="Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum."
          showMoreButton
        />

        {messages.length === 0 && (
          <ChatMessage role="assistant" content={t('page.chat.chat.initial_message')} />
        )}

        {messages.map((msg, index) => (
          <ChatMessage
            key={index}
            role={msg.role}
            content={msg.content}
            timestamp={msg.timestamp}
          />
        ))}

        {streamingResponse && streamingResponse.trim() && (
          <ChatMessage role="assistant" content={streamingResponse} />
        )}

        {error && (
          <Box
            sx={{
              padding: 2,
              margin: 1,
              bgcolor: '#ffebee',
              color: '#c62828',
              borderRadius: 1,
              border: '1px solid #ef5350',
            }}
          >
            <Text variant="body2" sx={{ color: '#c62828' }}>
              <SafeMarkdown text={`⚠️ ${error}`} />
            </Text>
          </Box>
        )}

        {loading && !streamingResponse?.trim() && (
          <LoadingContainer>
            <CircularProgress size={16} />
            <Text variant="caption">
              <SafeMarkdown text={t('page.chat.chat.typing')} />
            </Text>
          </LoadingContainer>
        )}

        <div ref={messagesEndRef} />
      </ChatPaper>

      <SuggestionsContainer>
        <Suggestion
          disabled={loading}
          onClick={() => {
            onSuggestionClick(t('page.chat.chat.first_question'));
          }}
        >
          <SafeMarkdown text={t('page.chat.chat.first_question')} />
        </Suggestion>

        <Suggestion
          disabled={loading}
          onClick={() => {
            onSuggestionClick(t('page.chat.chat.second_question'));
          }}
        >
          <SafeMarkdown text={t('page.chat.chat.second_question')} />
        </Suggestion>

        <Suggestion
          disabled={loading}
          onClick={() => {
            onSuggestionClick(`${t('page.chat.chat.third_question')} ${municipalityName}.`);
          }}
        >
          <SafeMarkdown text={`${t('page.chat.chat.third_question')} ${municipalityName}.`} />
        </Suggestion>
      </SuggestionsContainer>

      <SendMessageContainer>
        <SendMessageContent>
          <StyledInput
            name="message"
            variant="outlined"
            type="text"
            fullWidth
            multiline
            rows={4}
            placeholder={t('placeholder.placeholder.chat.input')}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSendMessage();
              }
            }}
            height="100%"
          />

          <Button
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              onSendMessage();
            }}
            disabled={loading || !input.trim()}
          >
            <Send />
          </Button>
        </SendMessageContent>
      </SendMessageContainer>
    </ChatContainer>
  );
};
